"use client";
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fetchUiModels, UiModel } from '@/lib/models';
import { useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Box, Typography, Avatar } from '@mui/material';
import { createAgentRecord } from '@/lib/api';

export default function AgentModal({
  open,
  onClose,
  onSave,
  providerOptions,
  defaultModel = 'openai/gpt-5',
}: {
  open: boolean;
  onClose: () => void;
  onSave: (agent: any) => void;
  providerOptions: string[];
  defaultModel?: string;
}) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [system_prompt, setSystem_prompt] = useState('');
  const [provider, setProvider] = useState(providerOptions?.[0] || 'auto');
  const [model, setModel] = useState(defaultModel);
  const [models, setModels] = useState<UiModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('auto');
  const [temperature, setTemperature] = useState(0.0);
  const [classroom, setClassroom] = useState(false);
  const [category, setCategory] = useState('General');
  const [visibility, setVisibility] = useState<'private' | 'public'>('private');
  const [avatar, setAvatar] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  // Hooks must not be conditional; compute memoized values before any early return
  const categories = useMemo(() => ['General', 'Education', 'Support', 'Data', 'Custom'], []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const arr = await fetchUiModels();
        if (!cancelled) {
          setModels(arr);
          try {
            const stored = localStorage.getItem('selectedModel');
            setSelectedModel(stored || arr[0]?.id || 'auto');
          } catch { setSelectedModel(arr[0]?.id || 'auto'); }
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Important: perform early return only after all hooks above are declared
  if (!open) return null;

  const uploadIcon = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/png,image/jpeg';
      input.onchange = async () => {
        const f = input.files?.[0];
        if (!f) return;
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append('file', f, f.name);
          const res = await fetch('/api/files/upload', { method: 'POST', body: fd });
          const data = await res.json();
          if (res.ok && data?.url) setAvatar(data.url);
        } finally { setBusy(false); }
      };
      input.click();
    } catch {}
  };

  const handleSave = async () => {
  const chosen = models.find(m => m.id === selectedModel);
  const agent = {
    name: title.trim(),
    description: desc.trim(),
    system_prompt: system_prompt.trim(), // Add system field for compatibility
    provider: chosen?.provider || provider,
    model: (chosen?.id || model).trim(),
    temperature,
    category,
    visibility,
    is_active: visibility === 'public', // true = visible to others, false = not visible
    avatar,
  };

  try {
    setBusy(true);
    // Use the API function from api.ts instead of direct fetch
    const data = await createAgentRecord(agent);
    console.log("✅ Agent created:", data);
    onSave?.(data); // ส่งข้อมูลกลับให้ parent
    onClose();
  } catch (err) {
    console.error("❌ Agent create error:", err);
    alert("ไม่สามารถสร้าง Agent ได้");
  } finally {
    setBusy(false);
  }
};

  const overlay = (
    <div className="fixed inset-0 z-[2000] bg-black/60 flex items-center justify-center cursor-pointer" onClick={onClose}>
      <div className="w-[min(900px,95vw)] max-h-[92vh] overflow-y-auto rounded-lg border border-gray-700 bg-gray-900 text-gray-100" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="font-semibold">Add Agent Ai</div>
          <button onClick={onClose} className="px-2 py-1 rounded hover:bg-gray-800">✕</button>
        </div>
        <div className="px-4 pt-3">
          <div className="flex gap-6 text-sm border-b border-gray-700">
            <div className="px-2 py-2 border-b-2 border-blue-400">Setting</div>
            <div className="px-2 py-2 text-gray-500">History</div>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={classroom} onChange={(e)=>setClassroom(e.target.checked)} /> ใช้สำหรับสอนในชั้นเรียน
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <FormControl size="small" variant="outlined" sx={{ minWidth: 280, width: '100%', '& .MuiInputBase-root': { color: '#E5E7EB', backgroundColor: '#11182799', borderRadius: 1 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#374151' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6B7280' }, '.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366F1' }, '& .MuiSvgIcon-root': { color: '#9CA3AF' } }}>
                <InputLabel sx={{ color: '#9CA3AF' }}>Model</InputLabel>
                <Select 
                  label="Model" 
                  value={selectedModel}
                  onChange={(e)=> { const v = String(e.target.value); setSelectedModel(v); try { localStorage.setItem('selectedModel', v); } catch {} }}
                  MenuProps={{ disablePortal: true }}
                  renderValue={() => {
                  const sel = models.find(m => m.id === selectedModel) || models[0];
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                      {sel?.logo ? (
                        <Avatar src={sel.logo} sx={{ width: 28, height: 28 }} />
                      ) : (
                        <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: sel?.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                          {sel?.badge}
                        </Box>
                      )}
                      <Typography sx={{ fontSize: 14, color: '#E5E7EB' }}>{sel?.label}</Typography>
                    </Box>
                  );
                }}>
                  {models.map(m => (
                    <MenuItem key={m.id} value={m.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                        {m.logo ? <Avatar src={m.logo} sx={{ width: 28, height: 28 }} /> : (
                          <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: m.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{m.badge}</Box>
                        )}
                        <Typography sx={{ fontSize: 14 }}>{m.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div>
              <div className="flex items-center justify-between text-sm">
                <div>Temperature (Optional) <span className="text-gray-400">Default</span></div>
                <div className="text-blue-300 font-mono">{temperature.toFixed(2)}</div>
              </div>
              <input type="range" min={0} max={2} step={0.01} value={temperature} onChange={(e)=>setTemperature(Number(e.target.value))} className="w-full" />
              <div className="flex justify-between text-[11px] text-gray-500"><span>Low</span><span>Medium</span><span>High</span></div>
            </div>
          </div>

          <div>
            <Label className="mb-3">Title</Label>
            <Input placeholder="Enter Title" value={title} onChange={(e)=>setTitle(e.target.value)} />
          </div>
          <div>
            <Label className="mb-3">Description</Label>
            <textarea className="w-full bg-black/40 border border-gray-700 rounded-md p-2 min-h-[80px]" placeholder="Enter Description" value={desc} onChange={(e)=>setDesc(e.target.value)} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Label className="mb-3">System Instruction</Label>
              <span className="text-xs text-gray-400">ตัวอย่างการสร้าง System Instruction</span>
            </div>
            <textarea className="w-full bg-black/40 border border-gray-700 rounded-md p-2 min-h-[120px]" placeholder="Set the system instruction during model initialization" value={system_prompt} onChange={(e)=>setSystem_prompt(e.target.value)} />
          </div>

          <div>
            <Label className="mb-3">Select Category</Label>
            <select value={category} onChange={(e)=>setCategory(e.target.value)} className="w-full bg-black/40 border border-gray-700 rounded-md px-2 py-2">
              {categories.map((c)=> <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" disabled={busy} onClick={uploadIcon}>Upload Icon</Button>
            {avatar && <span className="text-xs text-gray-400">Saved to {avatar}</span>}
          </div>

          <div>
            <div className="text-sm mb-1">Shared</div>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="radio" name="vis2" checked={visibility==='private'} onChange={()=>setVisibility('private')} /> Private</label>
              <label className="flex items-center gap-2"><input type="radio" name="vis2" checked={visibility==='public'} onChange={()=>setVisibility('public')} /> Public</label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 pb-4">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={!title.trim() || !model.trim() || busy}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
  return typeof window !== 'undefined' ? createPortal(overlay, document.body) : overlay;
}
